/*
  Refill & Replacement Parts Finder.

  The section server-renders every family panel, every card skeleton and
  every range row from the compatibility data, and renders them VISIBLE.
  This file hides the results on init and filters on selection — so with JS
  off the customer still gets every family, every badge and every working
  product link, rather than a dead set of radio buttons.

  What cannot be server-rendered is price, stock and image: `all_products` is
  capped at 20 lookups per page and the finder covers 44 products. Those three
  come from /products/{handle}.js on demand. Nothing is hardcoded either way,
  and a card that never enriches still shows its SKU, badges and product link.
*/
(() => {
  const root = document.querySelector('[data-rf]');
  if (!root) return;

  const cache = new Map();
  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  const money = (pence, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(pence / 100);
    } catch {
      return (pence / 100).toFixed(2);
    }
  };

  /** "Dual" -> 2, "6 per card" -> 6, "Single"/"Each"/"" -> 1. */
  const packCount = (pack) => {
    if (!pack) return 1;
    const digits = pack.match(/\d+/);
    if (digits) return parseInt(digits[0], 10);
    return /dual/i.test(pack) ? 2 : 1;
  };

  const fetchProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`${window.Shopify?.routes?.root ?? '/'}products/${handle}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return cache.get(handle);
  };

  // ---------------------------------------------------------------- lookups

  const typeInputs = root.querySelectorAll('[data-rf-type]');
  const rangeChoices = root.querySelectorAll('[data-rf-range-choice]');
  const rangeInputs = root.querySelectorAll('[data-rf-range]');
  const results = root.querySelector('[data-rf-results]');
  const resultsHeading = root.querySelector('[data-rf-results-heading]');
  /*
    [data-rf-key] is not decoration. The section-3 chips also name a family,
    and when they carried data-rf-family too this selector swept them up with
    the panels — so the init pass below set hidden on every chip and the old
    matrix rendered as a grid of empty cells. Panels are the things with a
    key; chips carry data-rf-chip-family instead and cannot collide.
  */
  const families = root.querySelectorAll('[data-rf-family][data-rf-key]');
  const stepOne = root.querySelector('[data-rf-step="1"]');
  const stepTwo = root.querySelector('[data-rf-step-two]');
  const empty = root.querySelector('[data-rf-empty]');

  const state = { type: null, range: null };

  const listed = (el, key, value) => (el.dataset[key] || '').split(' ').includes(value);

  // Take over from the no-JS view: hide the unfiltered results.
  results.hidden = true;
  families.forEach((panel) => {
    panel.hidden = true;
  });

  function showRangesForType() {
    let visible = 0;
    rangeChoices.forEach((choice) => {
      const ok = listed(choice, 'rfTypes', state.type);
      choice.hidden = !ok;
      if (ok) visible += 1;
      const input = choice.querySelector('input');
      // A hidden control must not stay selected, or the result set lies.
      if (!ok && input?.checked) {
        input.checked = false;
        state.range = null;
      }
    });
    if (stepTwo) stepTwo.hidden = visible === 0;
  }

  function markSteps() {
    if (stepOne) stepOne.dataset.rfDone = state.type ? 'true' : 'false';
    if (stepTwo) stepTwo.dataset.rfDone = state.range ? 'true' : 'false';
  }

  function render() {
    markSteps();

    if (!state.type || !state.range) {
      results.hidden = true;
      return;
    }

    let shown = 0;
    families.forEach((panel) => {
      const ok =
        listed(panel, 'rfTypes', state.type) && listed(panel, 'rfRanges', state.range);
      panel.hidden = !ok;
      if (ok) {
        shown += 1;
        enrich(panel);
      }
    });

    results.hidden = false;
    if (empty) empty.hidden = shown > 0;

    if (resultsHeading) {
      /*
        Name the pen type as well as the range. Two questions were answered but
        only the second was echoed back, so "What fits your ATX" gave no sign
        that the list had also been narrowed to a ballpoint - and an ATX is sold
        as a ballpoint, a rollerball and a fountain pen, so the reader had no way
        to tell which of the three they were looking at.
      */
      const rangeName = root.querySelector(`[data-rf-range][value="${state.range}"]`)
        ?.dataset.rfRangeName;
      const typeName = root.querySelector(`[data-rf-type][value="${state.type}"]`)?.dataset
        .rfTypeName;
      const what = [rangeName, typeName].filter(Boolean).join(' ');
      resultsHeading.textContent = shown
        ? `What fits your ${what}`
        : 'Nothing listed for that combination';
    }
  }

  typeInputs.forEach((input) =>
    input.addEventListener('change', () => {
      state.type = input.value;
      showRangesForType();
      render();
    })
  );

  rangeInputs.forEach((input) =>
    input.addEventListener('change', () => {
      state.range = input.value;
      render();
    })
  );

  // ------------------------------------------------------- chip navigation

  /*
    A chip in section 3 names one refill family for one range. Rather than
    dropping the customer on a bare anchor, drive section 1 to that pair
    first, so they land on "What fits your ATX ballpoint" with the products
    filtered — the same state they would have reached by answering the two
    questions themselves.

    Only chips that carry data-rf-chip are handled. The consumable chips
    that point straight at a product page have no such attribute and are
    left alone as ordinary links. With JS off every chip is an ordinary
    link too, and the family panels render visible, so the anchors resolve.

    Note the attribute names: data-rf-chip-family, NOT data-rf-family. The
    panel selector above matches on data-rf-family, and when the chips
    carried it too they were swept into the init pass and hidden — which is
    what made the old compatibility table render as a grid of blank cells.
  */
  root.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-rf-chip]');
    if (!chip) return;

    const rangeKey = chip.dataset.rfChipRange;
    const familyKey = chip.dataset.rfChipFamily;
    const panel = root.querySelector(`[data-rf-family][data-rf-key="${familyKey}"]`);
    const rangeInput = root.querySelector(`[data-rf-range][value="${rangeKey}"]`);
    if (!panel || !rangeInput) return;

    // The family may serve several pen types; use the first one it declares.
    const type = (panel.dataset.rfTypes || '').split(' ')[0];
    const typeInput = root.querySelector(`[data-rf-type][value="${type}"]`);
    if (!typeInput) return;

    event.preventDefault();

    typeInput.checked = true;
    state.type = type;
    showRangesForType();
    rangeInput.checked = true;
    state.range = rangeKey;
    render();

    panel.scrollIntoView({ behavior: motionOk ? 'smooth' : 'auto', block: 'start' });
    const heading = panel.querySelector('h4');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  });

  /*
    IDENTIFICATION CARD -> THE PRODUCTS IN THAT FAMILY

    A card names a family, not a range, so it cannot drive the two questions
    the way a section 3 chip does - there is no range to answer with. It
    reveals the family's panel on its own instead.

    The type and range are cleared as it goes. render() only shows panels
    that match both, so leaving a stale selection checked would put a tick
    against "Century II" while the results below it show every Classic
    Ballpoint, whatever the pen. Clearing is the honest state: no filter is
    applied, and the heading names what is on screen. Picking a type or range
    afterwards hands control straight back to render().

    Cards for single-product families are ordinary product links and never
    reach here - they carry no data-rf-goto-family.
  */
  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-rf-goto-family]');
    if (!link) return;

    const familyKey = link.dataset.rfGotoFamily;
    const panel = root.querySelector(`[data-rf-family][data-rf-key="${familyKey}"]`);
    if (!panel) return;

    event.preventDefault();

    typeInputs.forEach((input) => {
      input.checked = false;
    });
    rangeInputs.forEach((input) => {
      input.checked = false;
    });
    state.type = null;
    state.range = null;
    showRangesForType();
    markSteps();

    families.forEach((other) => {
      other.hidden = other !== panel;
    });
    enrich(panel);

    results.hidden = false;
    if (empty) empty.hidden = true;
    if (resultsHeading) {
      /*
        Not the bare family name: the panel underneath opens with its own h4
        carrying exactly that, and the two headings stacked read as a stutter.
        This keeps the voice of the default heading it replaces.
      */
      const name = link.dataset.rfGotoName;
      resultsHeading.textContent = name ? `Every ${name} we stock` : 'Every refill we stock';
    }

    panel.scrollIntoView({ behavior: motionOk ? 'smooth' : 'auto', block: 'start' });
    const target = panel.querySelector('h4');
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  });

  /*
    ENLARGE THE IDENTIFICATION PHOTOGRAPH

    One dialog serves all fourteen cards; opening swaps in the 1600px source
    and the family name. showModal() supplies the focus trap, Escape, the
    inert background and focus returned to the card that opened it, so the
    only things left to wire are the close button and a click on the
    backdrop.

    The backdrop is not a separate element - it is the dialog's own
    ::backdrop - so a click on it reports the dialog as the target. Comparing
    against the dialog itself is what separates "clicked outside" from
    "clicked the picture".
  */
  const zoomDialog = root.querySelector('[data-rf-zoom-dialog]');
  if (zoomDialog && typeof zoomDialog.showModal === 'function') {
    const zoomImg = zoomDialog.querySelector('[data-rf-zoom-img]');
    const zoomCaption = zoomDialog.querySelector('[data-rf-zoom-caption]');

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-rf-zoom]');
      if (!trigger) return;

      event.preventDefault();
      const name = trigger.dataset.rfZoomName || '';

      /*
        Drop the previous refill before the new source is set, so a slow
        decode shows an empty frame rather than the wrong pen. This was a
        listener on the dialog's close event until it was measured: close
        does not fire here - not for our dialog, not for a bare one built in
        the console, with both close() and showModal() native and unpatched -
        so the handler never ran. Doing it on open needs no event at all.
      */
      zoomImg.removeAttribute('src');
      zoomImg.src = trigger.dataset.rfZoomSrc || '';
      zoomImg.alt = name ? `${name}, enlarged` : '';
      if (zoomCaption) zoomCaption.textContent = name;
      zoomDialog.showModal();
    });

    zoomDialog.addEventListener('click', (event) => {
      if (event.target === zoomDialog || event.target.closest('[data-rf-zoom-close]')) {
        zoomDialog.close();
      }
    });

  }

  // ------------------------------------------------------------- enrichment

  async function enrich(panel) {
    if (panel.dataset.rfEnriched === 'true') return;
    panel.dataset.rfEnriched = 'true';

    const cards = [...panel.querySelectorAll('[data-rf-card]')];
    const loaded = await Promise.all(
      cards.map(async (card) => {
        const product = await fetchProduct(card.dataset.rfHandle);
        paint(card, product);
        return { card, product };
      })
    );

    nudgeBestValue(loaded);
  }

  function paint(card, product) {
    const priceEl = card.querySelector('[data-rf-price]');
    const statusEl = card.querySelector('[data-rf-status]');
    const mediaEl = card.querySelector('[data-rf-media]');
    const titleEl = card.querySelector('[data-rf-title]');

    if (!product) {
      // The link and SKU are already on the card; just stop promising a price.
      if (priceEl) priceEl.textContent = '';
      if (statusEl) statusEl.textContent = 'See product page for price';
      return;
    }

    if (titleEl && product.title) titleEl.textContent = product.title;

    if (mediaEl && product.featured_image) {
      const img = document.createElement('img');
      img.src = `${product.featured_image.split('?')[0]}?width=400`;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      mediaEl.replaceChildren(img);
    }

    const variant = product.variants?.find((v) => v.available) ?? product.variants?.[0];
    const per = packCount(card.dataset.rfPack);

    if (priceEl && variant) {
      priceEl.textContent = money(variant.price, product.currency);
      if (per > 1) {
        const each = document.createElement('span');
        each.className = 'rf-card__per';
        each.textContent = `${money(Math.round(variant.price / per), product.currency)} per refill`;
        priceEl.append(each);
      }
      card.dataset.rfUnit = String(variant.price / per);
    }

    const available = Boolean(variant?.available);
    card.classList.toggle('rf-card--unavailable', !available);
    if (statusEl) statusEl.textContent = available ? '' : 'Out of stock';
  }

  /** Where a single and a multi pack sit side by side, say which is cheaper. */
  function nudgeBestValue(loaded) {
    const groups = new Map();
    loaded.forEach(({ card }) => {
      if (!card.dataset.rfUnit || !card.dataset.rfGroup) return;
      groups.set(card.dataset.rfGroup, (groups.get(card.dataset.rfGroup) || []).concat(card));
    });

    groups.forEach((cards) => {
      if (cards.length < 2) return;
      const best = cards.reduce((a, b) =>
        parseFloat(a.dataset.rfUnit) <= parseFloat(b.dataset.rfUnit) ? a : b
      );
      if (cards.every((c) => c.dataset.rfUnit === best.dataset.rfUnit)) return;
      const flag = document.createElement('span');
      flag.className = 'rf-card__best';
      flag.textContent = 'Best value per refill';
      /*
        Inside the price element, not after it. Both layouts that use this
        are grids — the product card and the section 3 buy row — and a
        sibling inserted between the price and the button became a grid item
        of its own, sitting on a line of its own instead of beside the price.
      */
      best.querySelector('[data-rf-price]')?.append(flag);
    });
  }

  markSteps();
  root.dataset.rfEnhanced = 'true';
})();
